import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  Divider,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Card,
  CardContent,
  Checkbox,
  Tooltip
} from '@mui/material';
import {
  AccessTime as ClockIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Euro as EuroIcon,
  CalendarMonth as CalendarIcon,
  Person as PersonIcon,
  FilterList as FilterIcon,
  Calculate as CalculateIcon,
  Receipt as PayslipIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
//import Layout from '../components/common/Layout';
import api from '../services/api';

const AdminTimesheets = () => {
  const navigate = useNavigate();
  const [timesheets, setTimesheets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingTimesheet, setEditingTimesheet] = useState(null);  
 
 // Filters
  const [filters, setFilters] = useState({
    employee_id: '',
    status: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear()
  });

  // Selected timesheets for bulk actions
  const [selectedTimesheets, setSelectedTimesheets] = useState([]);
  
  // Salary calculation dialog
  const [openSalaryDialog, setOpenSalaryDialog] = useState(false);
  const [salaryCalculation, setSalaryCalculation] = useState(null);

  // Stats
  const [stats, setStats] = useState({
    pendingCount: 0,
    approvedCount: 0,
    totalHours: 0,
    totalOvertimeHours: 0
  });

  useEffect(() => {
    fetchEmployees();
    fetchTimesheets();
  }, [filters]);

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees');
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchTimesheets = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.employee_id) params.append('employee_id', filters.employee_id);
      if (filters.status) params.append('status', filters.status);
      params.append('month', filters.month);
      params.append('year', filters.year);

      const response = await api.get(`/timesheets?${params}`);
      setTimesheets(response.data.timesheets || []);
      setStats(response.data.stats || {
        pendingCount: 0,
        approvedCount: 0,
        totalHours: 0,
        totalOvertimeHours: 0
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching timesheets:', error);
      setLoading(false);
    }
  };

  const handleApprove = async (timesheetId) => {
    try {
      await api.put(`/timesheets/${timesheetId}/approve`);
      setSuccess('Timesheet approved successfully');
      fetchTimesheets();
    } catch (error) {
      setError('Failed to approve timesheet');
    }
  };

  const handleReject = async (timesheetId) => {
    try {
      await api.put(`/timesheets/${timesheetId}/reject`);
      setSuccess('Timesheet rejected');
      fetchTimesheets();
    } catch (error) {
      setError('Failed to reject timesheet');
    }
  };

const handleEdit = (timesheet) => {
  setEditingTimesheet({
    ...timesheet,
    work_date: timesheet.work_date.split('T')[0] // Format date for input
  });
  setOpenEditDialog(true);
};

const handleSaveEdit = async () => {
  try {
    await api.put(`/timesheets/${editingTimesheet.id}`, editingTimesheet);
    setSuccess('Timesheet updated successfully');
    setOpenEditDialog(false);
    fetchTimesheets();
  } catch (error) {
    setError('Failed to update timesheet');
  }
};

const calculateEditHours = () => {
  if (!editingTimesheet) return { total_hours: 0, overtime_hours: 0 };
  
  const start = new Date(`2000-01-01T${editingTimesheet.start_time}`);
  const end = new Date(`2000-01-01T${editingTimesheet.end_time}`);
  const diffMs = end - start;
  const diffHours = diffMs / (1000 * 60 * 60);
  const breakHours = parseInt(editingTimesheet.break_minutes) / 60;
  const totalHours = Math.max(0, diffHours - breakHours);
  const overtimeHours = Math.max(0, totalHours - 8);
  
  return {
    total_hours: totalHours.toFixed(2),
    overtime_hours: overtimeHours.toFixed(2)
  };
};

  const handleBulkApprove = async () => {
    try {
      await api.post('/timesheets/bulk-approve', { ids: selectedTimesheets });
      setSuccess(`${selectedTimesheets.length} timesheets approved`);
      setSelectedTimesheets([]);
      fetchTimesheets();
    } catch (error) {
      setError('Failed to approve timesheets');
    }
  };

  const calculateSalary = async () => {
    try {
      const response = await api.post('/salary/calculate', {
        employee_id: filters.employee_id,
        month: filters.month,
        year: filters.year
      });
      setSalaryCalculation(response.data);
      setOpenSalaryDialog(true);
    } catch (error) {
      setError('Failed to calculate salary');
    }
  };

  const generatePayslip = async (employeeId) => {
    try {
      await api.post('/payslips/generate', {
        employee_id: employeeId,
        month: filters.month,
        year: filters.year
      });
      setSuccess('Payslip generated successfully');
    } catch (error) {
      setError('Failed to generate payslip');
    }
  };

  const generateAllPayslips = async () => {
    try {
      await api.post('/payslips/generate-all', {
        month: filters.month,
        year: filters.year
      });
      setSuccess('All payslips generated successfully');
    } catch (error) {
      setError('Failed to generate payslips');
    }
  };

  const handleSelectTimesheet = (id) => {
    setSelectedTimesheets(prev =>
      prev.includes(id) 
        ? prev.filter(tid => tid !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedTimesheets(timesheets.filter(t => t.status === 'pending').map(t => t.id));
    } else {
      setSelectedTimesheets([]);
    }
  };

  const getStatusChip = (status) => {
    switch(status) {
      case 'approved':
        return <Chip icon={<ApproveIcon />} label="Approved" color="success" size="small" />;
      case 'pending':
        return <Chip label="Pending" color="warning" size="small" />;
      case 'rejected':
        return <Chip label="Rejected" color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('lt-LT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  // Group timesheets by employee
  const groupedTimesheets = timesheets.reduce((acc, timesheet) => {
    const key = timesheet.employee_id;
    if (!acc[key]) {
      acc[key] = {
        employee: timesheet.employee_name,
        timesheets: [],
        totalHours: 0,
        totalOvertimeHours: 0,
        pendingCount: 0
      };
    }
    acc[key].timesheets.push(timesheet);
    acc[key].totalHours += parseFloat(timesheet.total_hours || 0);
    acc[key].totalOvertimeHours += parseFloat(timesheet.overtime_hours || 0);
    if (timesheet.status === 'pending') acc[key].pendingCount++;
    return acc;
  }, {});

  return (
        <Box sx={{ height: '100%', overflow: 'auto', pb: 4 }}>
      <Container maxWidth="xl">
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            Timesheet Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review, approve timesheets and calculate salaries
          </Typography>
        </Box>

        {/* Alerts */}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ClockIcon sx={{ color: '#ed6c02', mr: 1 }} />
                  <Typography color="text.secondary" variant="body2">
                    Pending Approval
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#ed6c02' }}>
                  {stats.pendingCount}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ApproveIcon sx={{ color: '#2e7d32', mr: 1 }} />
                  <Typography color="text.secondary" variant="body2">
                    Approved
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                  {stats.approvedCount}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ClockIcon sx={{ color: '#1976d2', mr: 1 }} />
                  <Typography color="text.secondary" variant="body2">
                    Total Hours
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
                  {stats.totalHours}h
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <EuroIcon sx={{ color: '#9c27b0', mr: 1 }} />
                  <Typography color="text.secondary" variant="body2">
                    Overtime Hours
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#9c27b0' }}>
                  {stats.totalOvertimeHours}h
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Employee</InputLabel>
                <Select
                  value={filters.employee_id}
                  onChange={(e) => setFilters({...filters, employee_id: e.target.value})}
                  label="Employee"
                >
                  <MenuItem value="">All Employees</MenuItem>
                  {employees.map(emp => (
                    <MenuItem key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => setFilters({...filters, status: e.target.value})}
                  label="Status"
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Month</InputLabel>
                <Select
                  value={filters.month}
                  onChange={(e) => setFilters({...filters, month: e.target.value})}
                  label="Month"
                >
                  {[...Array(12)].map((_, i) => (
                    <MenuItem key={i+1} value={i+1}>
                      {new Date(2000, i).toLocaleString('default', { month: 'long' })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Year"
                type="number"
                value={filters.year}
                onChange={(e) => setFilters({...filters, year: e.target.value})}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<CalculateIcon />}
                  onClick={calculateSalary}
                  disabled={!filters.employee_id}
                  size="small"
                >
                  Calculate Salary
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<PayslipIcon />}
                  onClick={generateAllPayslips}
                  size="small"
                >
                  Generate Payslips
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
            <Tab label="Individual View" />
            <Tab label="Employee Summary" />
          </Tabs>
        </Paper>

        {/* Content based on tab */}
        {tabValue === 0 ? (
          // Individual Timesheets View
          <Paper sx={{ p: 2 }}>
            {selectedTimesheets.length > 0 && (
              <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="body2">
                  {selectedTimesheets.length} timesheets selected
                </Typography>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<ApproveIcon />}
                  onClick={handleBulkApprove}
                  sx={{ mt: 1 }}
                >
                  Approve Selected
                </Button>
              </Box>
            )}
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedTimesheets.length > 0 && selectedTimesheets.length < timesheets.filter(t => t.status === 'pending').length}
                        checked={selectedTimesheets.length > 0 && selectedTimesheets.length === timesheets.filter(t => t.status === 'pending').length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Employee</TableCell>
                    <TableCell>Start</TableCell>
                    <TableCell>End</TableCell>
                    <TableCell>Break</TableCell>
                    <TableCell>Total Hours</TableCell>
                    <TableCell>Overtime</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {timesheets.map((timesheet) => (
                    <TableRow key={timesheet.id}>
                      <TableCell padding="checkbox">
                        {timesheet.status === 'pending' && (
                          <Checkbox
                            checked={selectedTimesheets.includes(timesheet.id)}
                            onChange={() => handleSelectTimesheet(timesheet.id)}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {new Date(timesheet.work_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>{timesheet.employee_name}</TableCell>
                      <TableCell>{timesheet.start_time}</TableCell>
                      <TableCell>{timesheet.end_time}</TableCell>
                      <TableCell>{timesheet.break_minutes} min</TableCell>
                      <TableCell>
                        <strong>{timesheet.total_hours}h</strong>
                      </TableCell>
                      <TableCell>
                        {timesheet.overtime_hours > 0 && (
                          <Chip label={`+${timesheet.overtime_hours}h`} size="small" color="secondary" />
                        )}
                      </TableCell>
                      <TableCell>{timesheet.work_type}</TableCell>
                      <TableCell>{getStatusChip(timesheet.status)}</TableCell>
		     <TableCell>
  <Box sx={{ display: 'flex', gap: 1 }}>
    <Button
      size="small"
      variant="outlined"
      startIcon={<EditIcon />}
      onClick={() => handleEdit(timesheet)}
    >
      Edit
    </Button>
    {timesheet.status === 'pending' && (
      <>
        <Button
          size="small"
          variant="contained"
          color="success"
          startIcon={<ApproveIcon />}
          onClick={() => handleApprove(timesheet.id)}
        >
          Approve
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="error"
          startIcon={<RejectIcon />}
          onClick={() => handleReject(timesheet.id)}
        >
          Reject
        </Button>
      </>
    )}
  </Box>
</TableCell>	 
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ) : (
          // Employee Summary View
          <Grid container spacing={3}>
            {Object.entries(groupedTimesheets).map(([employeeId, data]) => (
              <Grid item xs={12} md={6} key={employeeId}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">
                        {data.employee}
                      </Typography>
                      {data.pendingCount > 0 && (
                        <Chip label={`${data.pendingCount} pending`} color="warning" size="small" />
                      )}
                    </Box>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Total Hours</Typography>
                        <Typography variant="h6">{data.totalHours.toFixed(2)}h</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Overtime</Typography>
                        <Typography variant="h6">{data.totalOvertimeHours.toFixed(2)}h</Typography>
                      </Grid>
                    </Grid>
                    
                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setFilters({...filters, employee_id: employeeId})}
                      >
                        View Details
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => generatePayslip(employeeId)}
                      >
                        Generate Payslip
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Salary Calculation Dialog */}
        <Dialog open={openSalaryDialog} onClose={() => setOpenSalaryDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Salary Calculation</DialogTitle>
          <DialogContent>
            {salaryCalculation && (
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Employee</Typography>
                    <Typography variant="h6">{salaryCalculation.employee_name}</Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">Total Hours</Typography>
                    <Typography>{salaryCalculation.total_hours}h</Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">Hourly Rate</Typography>
                    <Typography>{formatCurrency(salaryCalculation.hourly_rate)}</Typography>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">Base Salary</Typography>
                    <Typography>{formatCurrency(salaryCalculation.base_salary)}</Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">Overtime Pay</Typography>
                    <Typography>{formatCurrency(salaryCalculation.overtime_pay)}</Typography>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Gross Salary</Typography>
                    <Typography variant="h5">{formatCurrency(salaryCalculation.gross_salary)}</Typography>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">SODRA Employee</Typography>
                    <Typography color="error">-{formatCurrency(salaryCalculation.sodra_employee)}</Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">Income Tax</Typography>
                    <Typography color="error">-{formatCurrency(salaryCalculation.income_tax)}</Typography>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary">Net Salary</Typography>
                      <Typography variant="h4" color="primary">
                        {formatCurrency(salaryCalculation.net_salary)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenSalaryDialog(false)}>Close</Button>
            <Button variant="contained" onClick={() => generatePayslip(salaryCalculation?.employee_id)}>
              Generate Payslip
            </Button>
          </DialogActions>
        </Dialog>
	{/* Edit Timesheet Dialog */}
<Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth>
  <DialogTitle>Edit Timesheet</DialogTitle>
  <DialogContent>
    {editingTimesheet && (
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Employee"
            value={editingTimesheet.employee_name || ''}
            disabled
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Work Date"
            type="date"
            value={editingTimesheet.work_date}
            onChange={(e) => setEditingTimesheet({...editingTimesheet, work_date: e.target.value})}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Start Time"
            type="time"
            value={editingTimesheet.start_time}
            onChange={(e) => setEditingTimesheet({...editingTimesheet, start_time: e.target.value})}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="End Time"
            type="time"
            value={editingTimesheet.end_time}
            onChange={(e) => setEditingTimesheet({...editingTimesheet, end_time: e.target.value})}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Break (minutes)"
            type="number"
            value={editingTimesheet.break_minutes}
            onChange={(e) => setEditingTimesheet({...editingTimesheet, break_minutes: e.target.value})}
          />
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel>Work Type</InputLabel>
            <Select
              value={editingTimesheet.work_type}
              onChange={(e) => setEditingTimesheet({...editingTimesheet, work_type: e.target.value})}
              label="Work Type"
            >
              <MenuItem value="regular">Regular</MenuItem>
              <MenuItem value="overtime">Overtime</MenuItem>
              <MenuItem value="holiday">Holiday</MenuItem>
              <MenuItem value="sick">Sick Leave</MenuItem>
              <MenuItem value="vacation">Vacation</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Location"
            value={editingTimesheet.location || ''}
            onChange={(e) => setEditingTimesheet({...editingTimesheet, location: e.target.value})}
          />
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
            <Typography variant="body2" color="text.secondary">
              Calculated Hours: <strong>{calculateEditHours().total_hours}</strong>
            </Typography>
            {parseFloat(calculateEditHours().overtime_hours) > 0 && (
              <Typography variant="body2" color="secondary">
                Overtime: <strong>{calculateEditHours().overtime_hours}</strong>
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    )}
  </DialogContent>
  <DialogActions>
    <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
    <Button 
      onClick={handleSaveEdit} 
      variant="contained" 
      color="primary"
    >
      Save Changes
    </Button>
  </DialogActions>
</Dialog> 
     </Container>
      </Box>
  );
};

export default AdminTimesheets;
